<template>
  <div>
    <v-btn color="primary" @click="getPosition" v-if="gpsCheckinEnabled">
      <v-icon small left>{{icons.mdiMapMarkerRadius}}</v-icon>
      <span>{{$t('checkin.buttonText')}}</span>
    </v-btn>

    <v-dialog v-model="showGpsCheckinDialog" persistent max-width="320">
      <v-card>
        <v-card-title>
          <h4 class="subtitle-1" v-if="showErrorMessage">{{ errorTitle }}</h4>
          <h4 class="subtitle-1" v-else>{{ $t('checkin.checkinModalText') }}</h4>
        </v-card-title>
        <v-card-text>
          <div class="text-center" v-if="loader">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <v-alert dense type="error" class="caption" v-if="showErrorMessage">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeCheckinDialog">{{$t('checkin.checkinCancelButtonText')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showGpsInfoDialog" persistent max-width="320">
      <v-card class="gps-info-dialog">
        <v-card-title>
          <h4 class="title">{{ $t('checkin.whatIsACheckinTitle')}}</h4>
        </v-card-title>
        <v-card-text>
          {{ $t('checkin.whatIsACheckinText')}}
          <v-alert type="warning" dense class="caption mt-2">{{ $t('checkin.whatIsACheckinAlert')}}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showGpsInfoDialog = false">{{$t('checkin.checkinCancelButtonText')}}</v-btn>
          <v-btn color="primary" text @click="requestGpsCheckin">{{$t('checkin.checkinSubmitButtonText')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <congrats-dialog v-if="congratsDialog" :response="congratsData" :showDialog="congratsDialog" @hideDialog="closeCongratsDialog" />

  </div>
</template>

<script>
  import _ from 'lodash';
  import {BASE_API_URL, storage} from '@/store';
  import {mapGetters} from 'vuex';
  import {mdiMapMarkerRadius} from '@mdi/js';
  import CongratsDialog from '@/components/loyaltyscanner/CongratsDialog';
  import calculateWaitTime from '@/utils/calculateWaitTime';
  import isFeatureSupported from '@/utils/isFeatureSupported';

  export default {
    name: "GPSCheckin",
    components: {
      CongratsDialog
    },
    data() {
      return {
        loader: false,
        showGpsCheckinDialog: false,
        showGpsInfoDialog: false,
        errorTitle: this.$t('checkin.unexpectedErrorTitle'),
        errorMessage: '',
        showErrorMessage: false,
        icons: {mdiMapMarkerRadius},
        congratsDialog: false,
        congratsData: false,
        checkinWatchId: null,
        startWatchPositionTimeoutId: null,
        watchTimeout: 20 * 1000
      }
    },
    computed: {
      ...mapGetters(['extensionSettings', 'device', 'client_id']),
      gpsCheckinEnabled() {
        return _.get(this.extensionSettings, 'geofencing.gps_checkin_enabled', false) && isFeatureSupported('gpsCheckin');
      }
    },
    methods: {
      getPosition() {
        if (storage.getItem('agreedToGPSCheckin')) {
          this.requestGpsCheckin();
        } else {
          this.showGpsInfoDialog = true;
        }
      },

      requestGpsCheckin() {
        storage.setItem('agreedToGPSCheckin', 1);
        this.showGpsInfoDialog = false;

        this.showGpsCheckinDialog = true;
        this.loader = true;

        this.checkinWatchId = navigator.geolocation.watchPosition(
          (position) => {
            if (!this.startWatchPositionTimeoutId) {
              this.startWatchPositionTimeout();
            }
            this.requestCheckinLocation(position.coords.latitude, position.coords.longitude);
          },
          (error) => {
            console.error(error.message);
            if (error.code === 1) {
              this.errorTitle = this.$t('checkin.noLocationServiceTitle');
              this.errorMessage = this.$t('checkin.noLocationServiceText');
            } else if (error.code === 2 || error.code === 3) {
              this.errorTitle = this.$t('checkin.noLocationServiceTitle');
              this.errorMessage = this.$t('checkin.locatioNotDetermined')
            } else {
              this.errorMessage = this.$t('checkin.unexpectedError');
            }
            this.stopWatchPosition();
          }, {
            enableHighAccuracy: true,
            timeout: this.watchTimeout,
            maximumAge: 10 * 1000 // Always get a real position, we dont want cached values
          }
        );
      },
      requestCheckinLocation: _.throttle(async function(lat, lng) {
        try {
          const response = await this.axios.post(`${BASE_API_URL}/api/v1/customer-relationships/${this.client_id}/check-in/`, {gps_location: {lng, lat}});
          const data = response.data;

          if (data.success) {
            this.loader = false;
            this.showGpsCheckinDialog = false;
            this.congratsData = data;
            this.congratsDialog = true;
          } else {
            if (data.code === 'CHECKIN_THROTTLE_LIMIT_REACHED') {
              this.errorTitle = this.$t('checkin.checkinLimitReachedDialogTitle');
              if (_.get(data, 'wait', false)) {
                const {days, waitTime} = calculateWaitTime(_.get(data, 'wait', 0));
                this.errorMessage = this.$tc('checkin.checkinLimitReachedWithTimeWaitDialogText', days, {waitTime});
              } else {
                this.errorMessage = this.$t('checkin.checkinLimitReachedDialogText');
              }
            }
          }

          this.stopWatchPosition();

        } catch (error) {
          const {response} = error;
          this.errorMessage = this.$t('checkin.unexpectedError');
          if (response.data.code === 'CHECKIN_LOCATION_NOT_FOUND') {
            this.errorTitle = this.$t('checkin.noLocationServiceTitle');
            this.errorMessage = this.$t('checkin.checkinNotFound');
          }
        }
      }, 2000),
      startWatchPositionTimeout() {
        this.startWatchPositionTimeoutId = setTimeout(() => {
          this.stopWatchPosition();
          this.startWatchPositionTimeoutId = null;
        }, this.watchTimeout);
      },
      stopWatchPosition() {
        this.loader = false;
        this.showErrorMessage = !!this.errorMessage;

        if (Number.isInteger(this.startWatchPositionTimeoutId)) {
          clearTimeout(this.startWatchPositionTimeoutId);
          this.startWatchPositionTimeoutId = null;
        }

        if (Number.isInteger(this.checkinWatchId)) {
          navigator.geolocation.clearWatch(this.checkinWatchId);
          this.checkinWatchId = null;
        }
      },
      closeCheckinDialog() {
        this.stopWatchPosition();
        this.errorMessage = '';
        this.showErrorMessage = false;
        this.showGpsCheckinDialog = false;
      },
      closeCongratsDialog() {
        this.congratsDialog = false;
      },
    }
  }
</script>

<style lang="scss" scoped>
  .v-card {
    &::v-deep .v-card__text {
      padding-bottom: 0;
    }
  }
  .v-alert {
    margin-bottom: 0;
    ::v-deep .v-alert__wrapper i {
      align-self: center;
    }
    ::v-deep .v-alert__content {
      line-height: 1.4em;
    }
  }
</style>
